import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  EventRequest,
  EventResponse,
  EventAttendeeStatus,
  FileOwnerType,
  FileResponse,
  ChangePasswordRequest,
  DashboardSummaryResponse,
  InventoryItemResponse,
  InventoryMovementRequest,
  InventoryMovementResponse,
  NotificationResponse,
  PermissionMetadata,
  ProjectNoteEntry,
  ProjectNoteRequest,
  ProjectTaskNoteEntry,
  ProjectTaskNoteRequest,
  ProjectResponse,
  ProjectTaskRequest,
  ProjectTaskResponse,
  ProjectTimelineEntry,
  ResetPasswordRequest,
  RoleRequest,
  RoleResponse,
  SampleShipmentResponse,
  UnreadCountResponse,
  UserRequest,
  UserLookupResponse,
  UserResponse,
  UserStatusRequest
} from './models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  list<T = Record<string, unknown>>(path: string): Observable<T[]> {
    return this.http.get<T[]>(this.url(path));
  }

  get<T>(path: string): Observable<T> {
    return this.http.get<T>(this.url(path));
  }

  post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<T>(this.url(path), body);
  }

  put<T>(path: string, body: unknown): Observable<T> {
    return this.http.put<T>(this.url(path), body);
  }

  patch<T>(path: string, body: unknown): Observable<T> {
    return this.http.patch<T>(this.url(path), body);
  }

  delete<T = void>(path: string): Observable<T> {
    return this.http.delete<T>(this.url(path));
  }

  permissions(): Observable<PermissionMetadata[]> {
    return this.list<PermissionMetadata>('/roles/permissions');
  }

  roles(): Observable<RoleResponse[]> {
    return this.list<RoleResponse>('/roles');
  }

  createRole(request: RoleRequest): Observable<RoleResponse> {
    return this.post<RoleResponse>('/roles', request);
  }

  updateRole(roleId: string, request: RoleRequest): Observable<RoleResponse> {
    return this.put<RoleResponse>(`/roles/${roleId}`, request);
  }

  users(): Observable<UserResponse[]> {
    return this.list<UserResponse>('/users');
  }

  createUser(request: UserRequest): Observable<UserResponse> {
    return this.post<UserResponse>('/users', request);
  }

  updateUser(userId: string, request: UserRequest): Observable<UserResponse> {
    return this.patch<UserResponse>(`/users/${userId}`, request);
  }

  updateUserStatus(userId: string, request: UserStatusRequest): Observable<UserResponse> {
    return this.patch<UserResponse>(`/users/${userId}/status`, request);
  }

  resetUserPassword(userId: string, request: ResetPasswordRequest): Observable<void> {
    return this.patch<void>(`/users/${userId}/password/reset`, request);
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.patch<void>('/users/profile/password', request);
  }

  project(projectId: string): Observable<ProjectResponse> {
    return this.get<ProjectResponse>(`/projects/${projectId}`);
  }

  myProjects(): Observable<ProjectResponse[]> {
    return this.list<ProjectResponse>('/projects/mine');
  }

  myProjectTasks(): Observable<ProjectTaskResponse[]> {
    return this.list<ProjectTaskResponse>('/project-tasks/mine');
  }

  mySampleShipments(): Observable<SampleShipmentResponse[]> {
    return this.list<SampleShipmentResponse>('/sample-shipments/mine');
  }

  projectTasks(projectId: string): Observable<ProjectTaskResponse[]> {
    return this.list<ProjectTaskResponse>(`/projects/${projectId}/tasks`);
  }

  projectEvents(projectId: string): Observable<EventResponse[]> {
    return this.list<EventResponse>(`/projects/${projectId}/events`);
  }

  projectUsers(projectId: string): Observable<UserLookupResponse[]> {
    return this.list<UserLookupResponse>(`/projects/${projectId}/users`);
  }

  projectShipments(projectId: string): Observable<SampleShipmentResponse[]> {
    return this.list<SampleShipmentResponse>(`/sample-shipments/project/${projectId}`);
  }

  projectTimeline(projectId: string): Observable<ProjectTimelineEntry[]> {
    return this.list<ProjectTimelineEntry>(`/projects/${projectId}/timeline`);
  }

  addProjectTimelineEntry(projectId: string, entry: ProjectTimelineEntry): Observable<ProjectResponse> {
    return this.post<ProjectResponse>(`/projects/${projectId}/timeline`, entry);
  }

  projectNotes(projectId: string): Observable<ProjectNoteEntry[]> {
    return this.list<ProjectNoteEntry>(`/projects/${projectId}/notes`);
  }

  addProjectNote(projectId: string, request: ProjectNoteRequest): Observable<ProjectNoteEntry> {
    return this.post<ProjectNoteEntry>(`/projects/${projectId}/notes`, request);
  }

  updateProjectNote(projectId: string, noteId: string, request: ProjectNoteRequest): Observable<ProjectNoteEntry> {
    return this.put<ProjectNoteEntry>(`/projects/${projectId}/notes/${noteId}`, request);
  }

  deleteProjectNote(projectId: string, noteId: string): Observable<void> {
    return this.delete<void>(`/projects/${projectId}/notes/${noteId}`);
  }

  inviteProjectUsers(projectId: string, userIds: string[]): Observable<ProjectResponse> {
    return this.post<ProjectResponse>(`/projects/${projectId}/invite`, { userIds });
  }

  createProjectTask(projectId: string, request: ProjectTaskRequest): Observable<ProjectTaskResponse> {
    return this.post<ProjectTaskResponse>(`/projects/${projectId}/tasks`, request);
  }

  projectTaskNotes(taskId: string): Observable<ProjectTaskNoteEntry[]> {
    return this.list<ProjectTaskNoteEntry>(`/project-tasks/${taskId}/notes`);
  }

  addProjectTaskNote(taskId: string, request: ProjectTaskNoteRequest): Observable<ProjectTaskNoteEntry> {
    return this.post<ProjectTaskNoteEntry>(`/project-tasks/${taskId}/notes`, request);
  }

  updateProjectTaskNote(taskId: string, noteId: string, request: ProjectTaskNoteRequest): Observable<ProjectTaskNoteEntry> {
    return this.put<ProjectTaskNoteEntry>(`/project-tasks/${taskId}/notes/${noteId}`, request);
  }

  deleteProjectTaskNote(taskId: string, noteId: string): Observable<void> {
    return this.delete<void>(`/project-tasks/${taskId}/notes/${noteId}`);
  }

  notifications(): Observable<NotificationResponse[]> {
    return this.list<NotificationResponse>('/notifications');
  }

  unreadCount(): Observable<UnreadCountResponse> {
    return this.get<UnreadCountResponse>('/notifications/unread-count');
  }

  dashboardSummary(): Observable<DashboardSummaryResponse> {
    return this.get<DashboardSummaryResponse>('/dashboard/summary');
  }

  inventoryItem(inventoryItemId: string): Observable<InventoryItemResponse> {
    return this.get<InventoryItemResponse>(`/inventory-items/${inventoryItemId}`);
  }

  inventoryMovements(inventoryItemId: string): Observable<InventoryMovementResponse[]> {
    return this.list<InventoryMovementResponse>(`/inventory-items/${inventoryItemId}/movements`);
  }

  recordInventoryMovement(
    inventoryItemId: string,
    request: InventoryMovementRequest
  ): Observable<InventoryMovementResponse> {
    return this.post<InventoryMovementResponse>(`/inventory-items/${inventoryItemId}/movements`, request);
  }

  events(start: Date = offsetDays(new Date(), -90), end: Date = offsetDays(new Date(), 365)): Observable<EventResponse[]> {
    const params = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString()
    });
    return this.list<EventResponse>(`/events?${params.toString()}`);
  }

  myEvents(start: Date = offsetDays(new Date(), -90), end: Date = offsetDays(new Date(), 365)): Observable<EventResponse[]> {
    const params = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString()
    });
    return this.list<EventResponse>(`/events/mine?${params.toString()}`);
  }

  createEvent(request: EventRequest): Observable<EventResponse> {
    return this.post<EventResponse>('/events', request);
  }

  updateEvent(eventId: string, request: EventRequest): Observable<EventResponse> {
    return this.put<EventResponse>(`/events/${eventId}`, request);
  }

  respondToEvent(eventId: string, status: EventAttendeeStatus): Observable<EventResponse> {
    return this.post<EventResponse>(`/events/${eventId}/responses`, { status });
  }

  filesByOwner(ownerType: FileOwnerType, ownerId: string): Observable<FileResponse[]> {
    return this.list<FileResponse>(`/files/owner/${ownerType}/${ownerId}`);
  }

  uploadFiles(ownerType: FileOwnerType, ownerId: string, files: FileList | File[]): Observable<FileResponse[]> {
    const formData = new FormData();
    formData.append('ownerType', ownerType);
    formData.append('ownerId', ownerId);
    Array.from(files).forEach((file) => formData.append('files', file));
    return this.http.post<FileResponse[]>(this.url('/files/batch'), formData);
  }

  downloadFile(fileId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(this.url(`/files/${fileId}/download`), {
      observe: 'response',
      responseType: 'blob'
    });
  }

  private url(path: string): string {
    return `${environment.apiUrl}${path}`;
  }
}

function offsetDays(value: Date, days: number): Date {
  return new Date(value.getTime() + days * 24 * 60 * 60 * 1000);
}
